import {useSyncExternalStore} from "react";
import {COACHES} from "./coachCatalog";

export type CoachProductState = Record<string, boolean>;

const STORAGE_KEY = "par-coach-settings";

const createKey = (coachId: string, productId: string): string =>
    `${coachId}:${productId}`;

const loadFromStorage = (): CoachProductState | null => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? (JSON.parse(raw) as CoachProductState) : null;
    } catch {
        return null;
    }
};

const saveToStorage = (current: CoachProductState): void => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
    } catch {
        // storage unavailable — continue without persistence
    }
};

const buildInitialState = (): CoachProductState => {
    // All known products default to true so newly added coaches start enabled.
    const defaults: CoachProductState = {};
    for (const coach of COACHES) {
        for (const product of coach.products) {
            defaults[createKey(coach.id, product.id)] = true;
        }
    }
    const saved = loadFromStorage();
    // Merge saved values over defaults so unknown keys stay true.
    return saved ? {...defaults, ...saved} : defaults;
};

let state: CoachProductState = buildInitialState();
const listeners = new Set<() => void>();

const emit = (): void => {
    for (const listener of listeners) {
        listener();
    }
};

export const coachSettingsStore = {
    subscribe(listener: () => void): () => void {
        listeners.add(listener);
        return () => listeners.delete(listener);
    },
    getSnapshot(): CoachProductState {
        return state;
    },
    isProductEnabled(coachId: string, productId: string): boolean {
        return Boolean(state[createKey(coachId, productId)]);
    },
    toggleProduct(coachId: string, productId: string): void {
        const key = createKey(coachId, productId);
        state = {
            ...state,
            [key]: !state[key]
        };
        saveToStorage(state);
        emit();
    }
};

export function useCoachSettings(): CoachProductState {
    return useSyncExternalStore(
        coachSettingsStore.subscribe,
        coachSettingsStore.getSnapshot
    );
}
